import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CalendarX, Plus, Trash2, Settings } from "lucide-react";

export default function SlotBlocks() {
  const [blocks, setBlocks] = useState([]);
  const [cap, setCap] = useState({ weekday_pax: 40, weekend_pax: 60, interval_minutes: 30 });
  const [form, setForm] = useState({ date: "", time: "", reason: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [b, c] = await Promise.all([
        api.get("/staff/slot-blocks"),
        api.get("/staff/capacity"),
      ]);
      setBlocks(b.data);
      setCap(c.data);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.date) return toast.error("Date required");
    setBusy(true);
    try {
      await api.post("/staff/slot-blocks", form);
      toast.success(form.time ? `Blocked ${form.date} ${form.time}` : `Blocked all of ${form.date}`);
      setForm({ date: "", time: "", reason: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this block?")) return;
    try { await api.delete(`/staff/slot-blocks/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const saveCap = async () => {
    try {
      await api.put("/staff/capacity", {
        weekday_pax: Number(cap.weekday_pax) || 0,
        weekend_pax: Number(cap.weekend_pax) || 0,
        interval_minutes: Number(cap.interval_minutes) || 30,
      });
      toast.success("Capacity updated");
    } catch { toast.error("Save failed"); }
  };

  return (
    <div data-testid="slot-blocks-page">
      <div className="eyebrow">Availability</div>
      <h1 className="mt-2 text-4xl">Table capacity &amp; slot blocks</h1>
      <p className="mt-2 text-sm text-[hsl(var(--ink-soft))] max-w-2xl">
        The public reservation form checks these before letting a guest submit. Blocks show as "Not available";
        capacity caps trigger a "nearly full" warning when the remaining seats are less than the party size.
      </p>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-[1fr_1.4fr] gap-8">
        {/* Capacity settings */}
        <div className="p-5 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[hsl(var(--brass-2))]"><Settings size={14} /> Per-slot capacity</div>
          <div className="mt-4 space-y-3">
            <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
              Weekday pax (Mon-Thu)
              <input data-testid="cap-weekday" type="number" min="1" value={cap.weekday_pax} onChange={(e) => setCap({ ...cap, weekday_pax: e.target.value })} className="tbb-inp mt-1 w-full" />
            </label>
            <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
              Weekend pax (Fri-Sun)
              <input data-testid="cap-weekend" type="number" min="1" value={cap.weekend_pax} onChange={(e) => setCap({ ...cap, weekend_pax: e.target.value })} className="tbb-inp mt-1 w-full" />
            </label>
            <button data-testid="cap-save" onClick={saveCap} className="btn-brass !py-2 !px-4 text-sm">Save capacity</button>
          </div>
        </div>

        {/* Add block */}
        <div className="p-5 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[hsl(var(--brass-2))]"><CalendarX size={14} /> Block a date or slot</div>
          <form onSubmit={add} className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_2fr_auto] gap-2 items-end">
            <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
              Date *
              <input data-testid="block-date" required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="tbb-inp mt-1 w-full" />
            </label>
            <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
              Time (optional)
              <input data-testid="block-time" type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="tbb-inp mt-1 w-full" />
            </label>
            <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
              Reason
              <input data-testid="block-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Private event / staff off / repairs" className="tbb-inp mt-1 w-full" />
            </label>
            <button data-testid="block-add" type="submit" disabled={busy} className="btn-brass !py-2 !px-4 text-sm"><Plus size={14} /> Block</button>
          </form>
          <p className="mt-2 text-[10px] text-[hsl(var(--ink-soft))]">Leave time empty to block the whole day.</p>

          <div className="mt-5 border border-[hsl(var(--rule))]">
            <div className="px-3 py-2 text-[10px] uppercase tracking-wider bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))]">{blocks.length} active block(s)</div>
            <ul className="divide-y divide-[hsl(var(--rule))] max-h-80 overflow-auto">
              {blocks.length === 0 && <li className="p-4 text-sm text-[hsl(var(--ink-soft))]">No blocks yet — all slots open.</li>}
              {blocks.map((b) => (
                <li key={b.id} data-testid={`block-row-${b.id}`} className="p-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold text-sm">{b.date}{b.time ? ` · ${b.time}` : " · Full day"}</div>
                    {b.reason && <div className="text-xs text-[hsl(var(--ink-soft))]">{b.reason}</div>}
                  </div>
                  <button data-testid={`block-del-${b.id}`} onClick={() => remove(b.id)} className="text-[hsl(6_62%_45%)] hover:opacity-80"><Trash2 size={14} /></button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
    </div>
  );
}
